import React, { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Phone, Mail, Loader2, Upload, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formType, setFormType] = useState('contact'); // 'contact' or 'quote'
  const [photos, setPhotos] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    // Quote fields
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    service_interested: '',
  });

  const services = [
    '1-Step Paint Correction',
    '2-Step Paint Correction',
    'Scratch Removal',
    'Touch-Up Paint',
    'Ceramic Coating',
    'Headlight Restoration',
    'Interior Deep Cleaning',
    'Exterior Wash + Tire Shine',
    'Full Vehicle Polishing Package',
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 5) {
      toast.error('Maximum 5 photos allowed');
      return;
    }

    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotos(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
      });
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '', vehicle_make: '', vehicle_model: '', vehicle_year: '', service_interested: '' });
    } catch (error) {
      console.error('Contact error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/quotes`, {
        client_name: formData.name,
        client_email: formData.email,
        client_phone: formData.phone,
        vehicle_make: formData.vehicle_make,
        vehicle_model: formData.vehicle_model,
        vehicle_year: formData.vehicle_year,
        service_interested: formData.service_interested,
        message: formData.message,
        photos: photos,
      });
      toast.success('Quote request submitted! We\'ll review your photos and contact you within 24 hours.');
      setFormData({ name: '', email: '', phone: '', message: '', vehicle_make: '', vehicle_model: '', vehicle_year: '', service_interested: '' });
      setPhotos([]);
    } catch (error) {
      console.error('Quote error:', error);
      toast.error('Failed to submit quote request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-black">
      <Navigation />

      {/* Hero Section */}
      <section data-testid="contact-hero" className="relative pt-32 pb-12 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-red-600 rounded-full filter blur-[150px]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <h1 
            data-testid="contact-hero-title"
            className="text-5xl lg:text-6xl font-bold mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Get In <span className="text-red-500">Touch</span>
          </h1>
          <p data-testid="contact-hero-subtitle" className="text-lg text-gray-300 max-w-3xl mx-auto">
            Request a custom quote or send us a message
          </p>
        </div>
      </section>

      <section data-testid="contact-content" className="py-12 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div data-testid="contact-info" className="lg:col-span-1 space-y-6">
            <div className="glass-card p-6">
              <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Contact Information
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <MapPin className="text-red-500 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-gray-200">Service Area</p>
                    <p className="text-sm text-gray-400">Brooklyn, NY & Nearby Areas</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Phone className="text-red-500 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-gray-200">Phone</p>
                    <p className="text-sm text-gray-400">(929) 774-6344</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Mail className="text-red-500 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-gray-200">Email</p>
                    <p className="text-sm text-gray-400">onyxadetailing@gmail.com</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <h4 className="text-lg font-semibold mb-3">Hours</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <p>Monday - Saturday: 9:00 AM - 6:00 PM</p>
                <p>Sunday: By Appointment Only</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div data-testid="contact-form-container" className="lg:col-span-2">
            <div className="glass-card p-8">
              {/* Form Type Toggle */}
              <div data-testid="form-type-toggle" className="flex gap-4 mb-6">
                <button
                  data-testid="contact-tab-btn"
                  onClick={() => setFormType('contact')}
                  className={`flex-1 py-3 px-6 rounded-lg transition-all ${
                    formType === 'contact'
                      ? 'bg-gradient-to-r from-red-600 to-red-800 text-white'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  Send Message
                </button>
                <button
                  data-testid="quote-tab-btn"
                  onClick={() => setFormType('quote')}
                  className={`flex-1 py-3 px-6 rounded-lg transition-all ${
                    formType === 'quote'
                      ? 'bg-gradient-to-r from-red-600 to-red-800 text-white'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  Request Quote
                </button>
              </div>

              {/* Contact Form */}
              {formType === 'contact' && (
                <form onSubmit={handleContactSubmit} data-testid="contact-form" className="space-y-6">
                  <div data-testid="contact-name-container" className="space-y-2">
                    <Label htmlFor="name" className="text-gray-200">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="contact-name-input"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="bg-gray-900 border-gray-700 text-white"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div data-testid="contact-email-container" className="space-y-2">
                      <Label htmlFor="email" className="text-gray-200">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        data-testid="contact-email-input"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                    </div>

                    <div data-testid="contact-phone-container" className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-200">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        data-testid="contact-phone-input"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                    </div>
                  </div>

                  <div data-testid="contact-message-container" className="space-y-2">
                    <Label htmlFor="message" className="text-gray-200">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-message-textarea"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      className="bg-gray-900 border-gray-700 text-white min-h-[150px]"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white py-6"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </Button>
                </form>
              )}

              {/* Quote Form */}
              {formType === 'quote' && (
                <form onSubmit={handleQuoteSubmit} data-testid="quote-form" className="space-y-6">
                  <div data-testid="quote-name-container" className="space-y-2">
                    <Label htmlFor="name" className="text-gray-200">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="quote-name-input"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="bg-gray-900 border-gray-700 text-white"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div data-testid="quote-email-container" className="space-y-2">
                      <Label htmlFor="email" className="text-gray-200">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        data-testid="quote-email-input"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                    </div>

                    <div data-testid="quote-phone-container" className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-200">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        data-testid="quote-phone-input"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div data-testid="quote-year-container" className="space-y-2">
                      <Label htmlFor="vehicle_year" className="text-gray-200">Vehicle Year *</Label>
                      <Input
                        id="vehicle_year"
                        name="vehicle_year"
                        data-testid="quote-year-input"
                        value={formData.vehicle_year}
                        onChange={handleInputChange}
                        required
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                    </div>

                    <div data-testid="quote-make-container" className="space-y-2">
                      <Label htmlFor="vehicle_make" className="text-gray-200">Make *</Label>
                      <Input
                        id="vehicle_make"
                        name="vehicle_make"
                        data-testid="quote-make-input"
                        value={formData.vehicle_make}
                        onChange={handleInputChange}
                        required
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                    </div>

                    <div data-testid="quote-model-container" className="space-y-2">
                      <Label htmlFor="vehicle_model" className="text-gray-200">Model *</Label>
                      <Input
                        id="vehicle_model"
                        name="vehicle_model"
                        data-testid="quote-model-input"
                        value={formData.vehicle_model}
                        onChange={handleInputChange}
                        required
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                    </div>
                  </div>

                  <div data-testid="quote-service-container" className="space-y-2">
                    <Label htmlFor="service_interested" className="text-gray-200">Service Interested *</Label>
                    <Select value={formData.service_interested} onValueChange={(value) => setFormData(prev => ({ ...prev, service_interested: value }))}>
                      <SelectTrigger data-testid="quote-service-trigger" className="bg-gray-900 border-gray-700 text-white">
                        <SelectValue placeholder="Choose a service" />
                      </SelectTrigger>
                      <SelectContent data-testid="quote-service-content" className="bg-gray-900 border-gray-700">
                        {services.map((service, index) => (
                          <SelectItem key={index} value={service} data-testid={`quote-service-option-${index}`} className="text-white hover:bg-gray-800">
                            {service}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div data-testid="quote-photos-container" className="space-y-2">
                    <Label className="text-gray-200">Upload Vehicle Photos (Max 5)</Label>
                    <div className="flex flex-col gap-4">
                      <label
                        htmlFor="photo-upload"
                        data-testid="photo-upload-label"
                        className="flex items-center justify-center gap-2 w-full py-6 border-2 border-dashed border-gray-700 rounded-lg cursor-pointer hover:border-red-500 transition-colors"
                      >
                        <Upload size={20} className="text-gray-400" />
                        <span className="text-gray-400">Click to upload photos</span>
                        <input
                          id="photo-upload"
                          type="file"
                          data-testid="photo-upload-input"
                          accept="image/*"
                          multiple
                          onChange={handlePhotoUpload}
                          className="hidden"
                        />
                      </label>
                      {photos.length > 0 && (
                        <div data-testid="uploaded-photos-preview" className="grid grid-cols-3 gap-2">
                          {photos.map((photo, index) => (
                            <div key={index} data-testid={`photo-preview-${index}`} className="relative group">
                              <img src={photo} alt={`Upload ${index + 1}`} className="w-full h-24 object-cover rounded-lg" />
                              <button
                                type="button"
                                data-testid={`remove-photo-${index}`}
                                onClick={() => removePhoto(index)}
                                className="absolute top-1 right-1 bg-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div data-testid="quote-message-container" className="space-y-2">
                    <Label htmlFor="message" className="text-gray-200">Additional Details</Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="quote-message-textarea"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="bg-gray-900 border-gray-700 text-white min-h-[100px]"
                      placeholder="Describe the condition or any specific concerns..."
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="quote-submit-btn"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white py-6"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Request Quote'
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
